import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import EmojiPicker from './EmojiPicker';
import styles from './ChatBox.module.css';

interface ChatMessage {
  id?: number;
  userId?: number;
  mercenaryId?: number;
  userName?: string;
  mercenaryName?: string;
  message?: string;
  createdAt?: string;
  isSystem?: boolean;
}

interface ChatBoxProps {
  chatMessages: ChatMessage[];
  lobbyId: string;
  socket: any | null;
  userId: number;
  userName: string;
  mercenaryId: number | null;
  mercenaryName: string | null;
}

const ChatBox: React.FC<ChatBoxProps> = ({
  chatMessages, lobbyId, socket,
  userId, userName, mercenaryId, mercenaryName,
}) => {
  const [message, setMessage] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [reportedIds, setReportedIds] = useState<Set<number>>(new Set());
  const endRef = useRef<HTMLDivElement>(null);
  const isInitialLoad = useRef(true);

  useEffect(() => {
    // Skip auto-scroll on initial load, it pulls the whole page to the bottom
    // on mobile. Only scroll when new messages arrive after the first render.
    if (isInitialLoad.current) {
      isInitialLoad.current = false;
      return;
    }
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  useEffect(() => {
    if (!socket) return;
    const onChatError = (err: { message: string }) => {
      console.error('[ChatBox] chat_error received:', err.message);
    };
    socket.on('chat_error', onChatError);
    return () => { socket.off('chat_error', onChatError); };
  }, [socket]);

  const sendMessage = () => {
    const trimmed = message.trim();
    if (!trimmed || !socket) return;
    const parsedLobbyId = parseInt(lobbyId);
    //console.log('[ChatBox] sendMessage, lobbyId:', parsedLobbyId, 'mercenaryId:', mercenaryId, 'socket connected:', socket?.connected);
    socket.emit('chat_message_lobby', {
      lobbyId: parsedLobbyId,
      message: trimmed,
      mercenaryId,
    });
    setMessage('');
  };

  const handleKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') sendMessage();
  };

  const handleEmoji = (emoji: string) => {
    setMessage(prev => prev + `:${emoji}:`);
    setShowEmojiPicker(false);
  };

  const handleReport = async (chatId: number) => {
    try {
      await axios.post('/moderator/chat/report', { chatId, reason: 'Chat abuse' }, { withCredentials: true });
      setReportedIds(prev => new Set(prev).add(chatId));
    } catch (err: any) {
      // Already reported or error - just mark it
      setReportedIds(prev => new Set(prev).add(chatId));
    }
  };

  const renderMessageText = (text: string): React.ReactNode[] => {
    const result: React.ReactNode[] = [];
    // match :ID: tokens where ID is numeric
    const re = new RegExp(':([0-9]+):', 'g');
    let lastIndex = 0;
    let match;
    while ((match = re.exec(text)) !== null) {
      if (match.index > lastIndex) {
        result.push(<span key={`t${lastIndex}`}>{text.slice(lastIndex, match.index)}</span>);
      }
      result.push(<img key={`e${match.index}`} src={`/api/emojis/${match[1]}`} alt={`:${match[1]}:`} className={styles.chatEmoji} />);
      lastIndex = re.lastIndex;
    }
    if (lastIndex < text.length) {
      result.push(<span key={`t${lastIndex}`}>{text.slice(lastIndex)}</span>);
    }
    return result;
  };

  return (
    <div className={styles.chatWrapper}>
      <div className={styles.chatMessages}>
        {chatMessages.length === 0 && (
          <div className={styles.chatSystem}>No messages yet.</div>
        )}
        {chatMessages.map((msg, i) => {
          if (msg.isSystem) {
            return (
              <div key={i} className={styles.chatSystem}>
                <span className={styles.chatSystemText}>{msg.message}</span>
              </div>
            );
          }
          const sender = msg.mercenaryName || msg.userName || '';
          const alreadyReported = msg.id !== undefined && reportedIds.has(msg.id);
          return (
            <div key={msg.id ?? i} className={styles.chatMessage}>
              {msg.id !== undefined && msg.userId !== userId && (
                <button
                  className={`${styles.reportBtn} ${alreadyReported ? styles.reportBtnDone : ''}`}
                  onClick={() => !alreadyReported && handleReport(msg.id!)}
                  title={alreadyReported ? 'Reported' : 'Report message'}
                  type="button"
                >!</button>
              )}
              {msg.id !== undefined && msg.userId === userId && (
                <span className={styles.reportBtnPlaceholder} />
              )}
              {msg.createdAt && <span className={styles.chatTime}>{msg.createdAt}</span>}
              {msg.mercenaryId
                ? <Link to={`/mercenary/${msg.mercenaryId}`} className={styles.chatSenderLink}>{sender}:</Link>
                : <span className={styles.chatSender}>{sender}:</span>
              }
              <span className={styles.chatText}>{renderMessageText(msg.message ?? '')}</span>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>

      <div className={styles.chatInputRow}>
        <div className={styles.chatInputWrap}>
          <input
            className={styles.chatInputField}
            type="text"
            value={message}
            onChange={e => setMessage(e.target.value)}
            onKeyDown={handleKey}
            placeholder="Write a message…"
            maxLength={200}
          />
          <div className={styles.emojiButtonWrap}>
            <button
              className={styles.emojiToggle}
              onClick={() => setShowEmojiPicker(v => !v)}
              type="button"
              title="Emojis"
            >😀</button>
            {showEmojiPicker && (
              <div className={styles.emojiPickerWrapper}>
                <EmojiPicker onSelect={handleEmoji} />
              </div>
            )}
          </div>
        </div>
        <button className={styles.chatSendButton} onClick={sendMessage} type="button">Send</button>
      </div>

    </div>
  );
};

export default ChatBox;
